// ✅ ./HelperFunctions/formatNumber.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function FormattedNumberInput({
  value,
  onChange,
  readOnly = false,
  className = "",
  title = "",
  placeholder = "",
  min,
  decimals = 2,
  allowNegative = false,
}) {
  const [display, setDisplay] = useState("");
  const isFocusedRef = useRef(false);

  const regex = useMemo(() => {
    // limit decimals while typing: 0..decimals digits after "."
    const decPart = decimals > 0 ? `(?:\\.\\d{0,${decimals}})?` : "";
    return allowNegative
      ? new RegExp(`^-?\\d*${decPart}$`)
      : new RegExp(`^\\d*${decPart}$`);
  }, [decimals, allowNegative]);

  // Sync from parent -> display ONLY when not focused (prevents '.' being eaten)
  useEffect(() => {
    if (isFocusedRef.current) return;

    if (value === "" || value == null) {
      setDisplay("");
      return;
    }

    const raw = String(value).replace(/,/g, "").trim();
    if (raw === "" || raw === "-" || raw === ".") {
      setDisplay(raw);
      return;
    }

    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setDisplay("");
      return;
    }

    // show pretty formatting when not editing
    setDisplay(
      n.toLocaleString("en-GB", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    );
  }, [value, decimals]);

  const handleChange = (e) => {
    if (readOnly) return;

    const nextDisplay = e.target.value ?? "";
    const raw = nextDisplay.replace(/,/g, "");

    if (!regex.test(raw)) return;

    setDisplay(nextDisplay);
    // IMPORTANT: emit raw string so parent can store string during typing
    onChange?.(raw);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;

    // when user focuses, strip commas so caret behaviour is normal
    setDisplay((prev) => String(prev ?? "").replace(/,/g, ""));
  };

  const handleBlur = () => {
    if (readOnly) return;
    isFocusedRef.current = false;

    const raw = String(display ?? "")
      .replace(/,/g, "")
      .trim();

    // allow empty / incomplete states
    if (raw === "" || raw === "-" || raw === ".") {
      setDisplay("");
      onChange?.("");
      return;
    }

    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setDisplay("");
      onChange?.("");
      return;
    }

    // Optional: min enforcement
    if (min != null && Number.isFinite(Number(min)) && n < Number(min)) {
      const clamped = Number(min);
      const formattedMin = clamped.toLocaleString("en-GB", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      setDisplay(formattedMin);
      onChange?.(String(clamped));
      return;
    }

    const formatted = n.toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    setDisplay(formatted);
    // emit normalized numeric string
    onChange?.(String(n));
  };

  return (
    <input
      type="text"
      inputMode={decimals === 0 ? "numeric" : "decimal"}
      value={display}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      readOnly={readOnly}
      min={min}
      title={title}
      placeholder={placeholder}
      className={className}
    />
  );
}
